import { Fragment, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Button, ColumnManager, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import type { ProductInsightsEvent } from '@/lib/types';
import { formatCount, formatRate } from '../use-fab-data';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiExpandButton,
  PiSection,
  PiSortHeader,
} from '../pi-kit';
import {
  ariaSort,
  compareValues,
  eventDateText,
  eventPlaceText,
  parseSortState,
  eventKey,
  type EventsHeadline,
  type SortState,
} from './events-model';
import { EventSchoolsPanel, FormatBadge } from './events-ui';

/**
 * Events tab: one row per event, most recent first by default. A row expands
 * inline to the schools that took part (split Institutions / Partners).
 *
 * - Sort is client-side and remembered; ties fall back to the date, newest
 *   first. A missing value (no date, no attendance rate) sorts last.
 * - The totals row is the headline passed in (server totals when nothing
 *   narrows the list, else the sum of the visible rows), so it always agrees
 *   with the tiles. It always totals EVERY listed event, not just the rows
 *   shown before "Show more".
 * - `event` is the identifying column: code-pinned left, locked, and it holds
 *   the expand chevron, so the header row renders exactly one cell per visible
 *   key (`useFrozenOffsets` measures by position).
 */

type Column =
  | 'event'
  | 'date'
  | 'format'
  | 'location'
  | 'registrations'
  | 'attendees'
  | 'attendance'
  | 'schools';
type SortColumn = 'date' | 'event' | 'registrations' | 'attendees' | 'attendance' | 'schools';

const COLUMN_KEYS: readonly Column[] = Object.freeze([
  'event',
  'date',
  'format',
  'location',
  'registrations',
  'attendees',
  'attendance',
  'schools',
]);
const SORT_COLUMNS: readonly SortColumn[] = [
  'date',
  'event',
  'registrations',
  'attendees',
  'attendance',
  'schools',
];
const LOCKED_KEYS: readonly string[] = Object.freeze(['event']);
const NO_PINNED: readonly string[] = Object.freeze([]);
const LAYOUT_OPTIONS = Object.freeze({ pinnedLeftKeys: Object.freeze(['event']) });
const LAYOUT_SCOPE = 'product-insights.events.events';

const DEFAULT_SORT: SortState<SortColumn> = { column: 'date', dir: 'desc' };
const parseSort = parseSortState(SORT_COLUMNS);
const PAGE_SIZE = 50;

interface ColumnSpec {
  label: string;
  numeric: boolean;
  sort: SortColumn | null;
  className: string;
}

const COLUMNS: Record<Column, ColumnSpec> = {
  event: { label: 'Event', numeric: false, sort: 'event', className: 'min-w-64' },
  date: { label: 'Date', numeric: false, sort: 'date', className: 'min-w-36' },
  format: { label: 'Format', numeric: false, sort: null, className: 'min-w-24' },
  location: { label: 'Location', numeric: false, sort: null, className: 'min-w-40' },
  registrations: { label: 'Registrations', numeric: true, sort: 'registrations', className: 'min-w-28' },
  attendees: { label: 'Attendees', numeric: true, sort: 'attendees', className: 'min-w-24' },
  attendance: { label: 'Attendance', numeric: true, sort: 'attendance', className: 'min-w-28' },
  schools: { label: 'Schools', numeric: true, sort: 'schools', className: 'min-w-20' },
};

const labelOf = (key: string): string => COLUMNS[key as Column]?.label ?? key;
const lockReasonOf = (key: string): string | undefined =>
  key === 'event' ? 'The event name identifies each row.' : undefined;

function valueOf(event: ProductInsightsEvent, column: SortColumn): number | string | null {
  switch (column) {
    case 'date':
      return event.startDate;
    case 'event':
      return event.name;
    case 'registrations':
      return event.registrations;
    case 'attendees':
      return event.attendees;
    case 'attendance':
      return event.attendanceRate;
    case 'schools':
      return event.participatingSchoolsCount;
  }
}

function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => 'left' | 'right' | undefined,
  offsets: Record<string, number>,
): { frozen?: 'left' | 'right'; frozenOffset?: number } {
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

function EventName({ event }: { event: ProductInsightsEvent }): ReactElement {
  return (
    <div className="min-w-0">
      <div className="truncate font-medium text-fg">{event.name}</div>
      {event.subProductName && (
        <div className="truncate text-xs text-fg-muted">{event.subProductName}</div>
      )}
    </div>
  );
}

function bodyCell(column: Column, event: ProductInsightsEvent): ReactNode {
  switch (column) {
    case 'event':
      return <EventName event={event} />;
    case 'date':
      return eventDateText(event.startDate, event.endDate);
    case 'format':
      return <FormatBadge format={event.format} />;
    case 'location':
      return eventPlaceText(event);
    case 'registrations':
      return formatCount(event.registrations);
    case 'attendees':
      return formatCount(event.attendees);
    case 'attendance':
      return formatRate(event.attendanceRate);
    case 'schools':
      return formatCount(event.participatingSchoolsCount);
  }
}

function cellClass(column: Column): string {
  switch (column) {
    case 'event':
      return 'max-w-96';
    case 'date':
      return 'whitespace-nowrap tabular-nums';
    case 'location':
      return 'max-w-56 truncate';
    case 'registrations':
      return 'font-medium tabular-nums';
    case 'attendance':
      return 'tabular-nums text-fg-muted';
    case 'attendees':
    case 'schools':
      return 'tabular-nums';
    case 'format':
      return '';
  }
}

export function EventsTable({
  events,
  headline,
  onClearFilters,
}: {
  events: readonly ProductInsightsEvent[];
  headline: EventsHeadline;
  /** Clears the page's filters; offered from the empty state when given. */
  onClearFilters?: () => void;
}): ReactElement {
  const [sort, setSort] = useRememberedState<SortState<SortColumn>>(
    'pi-events-sort',
    DEFAULT_SORT,
    parseSort,
  );
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [limitFor, setLimitFor] = useState(events);
  if (limitFor !== events) {
    setLimitFor(events);
    setLimit(PAGE_SIZE);
  }

  const layout = useColumnLayout(LAYOUT_SCOPE, COLUMN_KEYS, LOCKED_KEYS, NO_PINNED, LAYOUT_OPTIONS);
  const { visibleKeys, frozenSideOf } = layout;
  const headRowRef = useRef<HTMLTableRowElement | null>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, visibleKeys, frozenSideOf, NO_PINNED);

  const rows = useMemo(
    () =>
      [...events].sort(
        (a, b) =>
          compareValues(valueOf(a, sort.column), valueOf(b, sort.column), sort.dir) ||
          compareValues(a.startDate, b.startDate, 'desc') ||
          a.name.localeCompare(b.name),
      ),
    [events, sort.column, sort.dir],
  );
  const shown = rows.slice(0, limit);
  const remaining = rows.length - shown.length;

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const onSort = (column: SortColumn) =>
    setSort(
      sort.column === column
        ? { column, dir: sort.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: column === 'event' ? 'asc' : 'desc' },
    );

  const columnManager = layout.loading ? undefined : (
    <ColumnManager
      layout={layout}
      labelOf={labelOf}
      lockedKeys={LOCKED_KEYS}
      lockReasonOf={lockReasonOf}
    />
  );

  const showMore =
    remaining > 0 ? (
      <div className="flex justify-center border-t border-border p-2">
        <Button
          variant="ghost"
          size="sm"
          className="max-sm:h-11"
          onClick={() => setLimit((current) => current + PAGE_SIZE)}
        >
          Show {formatCount(Math.min(PAGE_SIZE, remaining))} more of {formatCount(remaining)}
        </Button>
      </div>
    ) : null;

  if (rows.length === 0) {
    return (
      <PiSection title="Events" count={0} actions={columnManager} flush>
        <PiEmpty
          title="No events match these filters"
          description="Clear the filters or pick another cycle to see events."
          action={
            onClearFilters ? (
              <Button variant="secondary" className="max-sm:h-11" onClick={onClearFilters}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      </PiSection>
    );
  }

  const columnCount = visibleKeys.length;

  return (
    <PiSection title="Events" count={rows.length} actions={columnManager} flush>
      {/* Desktop */}
      <div className="max-md:hidden">
        <TableWrap frozenHeader>
          <Table>
            <THead>
              <TR ref={headRowRef}>
                {visibleKeys.map((key) => {
                  const spec = COLUMNS[key as Column];
                  if (!spec) return <TH key={key} />;
                  const sortKey = spec.sort;
                  const active = sortKey !== null && sort.column === sortKey;
                  return (
                    <TH
                      key={key}
                      numeric={spec.numeric}
                      className={spec.className}
                      aria-sort={active ? ariaSort(sort.dir) : undefined}
                      {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                    >
                      {sortKey === null ? (
                        spec.label
                      ) : (
                        <span className={key === 'event' ? 'pl-9' : undefined}>
                          <PiSortHeader
                            label={spec.label}
                            active={active}
                            direction={active ? sort.dir : 'desc'}
                            align={spec.numeric ? 'right' : 'left'}
                            onClick={() => onSort(sortKey)}
                          />
                        </span>
                      )}
                    </TH>
                  );
                })}
              </TR>
            </THead>
            <TBody>
              {shown.map((event) => {
                const id = eventKey(event);
                const open = expanded.has(id);
                return (
                  <Fragment key={id}>
                    <TR>
                      {visibleKeys.map((key) => {
                        const column = key as Column;
                        if (!COLUMNS[column]) return <TD key={key} />;
                        return (
                          <TD
                            key={key}
                            numeric={COLUMNS[column].numeric}
                            className={cellClass(column)}
                            {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                          >
                            {column === 'event' ? (
                              <div className="flex min-w-0 items-start gap-1">
                                <PiExpandButton
                                  expanded={open}
                                  onClick={() => toggle(id)}
                                  label={`${open ? 'Hide' : 'Show'} schools for ${event.name}`}
                                />
                                <div className="min-w-0 pt-1.5">
                                  <EventName event={event} />
                                </div>
                              </div>
                            ) : (
                              bodyCell(column, event)
                            )}
                          </TD>
                        );
                      })}
                    </TR>
                    {open && (
                      <TR>
                        <TD colSpan={columnCount} className="bg-surface-sunken p-0">
                          <div className="sticky left-0 w-full max-w-4xl px-4 py-3 pl-12">
                            <EventSchoolsPanel event={event} />
                          </div>
                        </TD>
                      </TR>
                    )}
                  </Fragment>
                );
              })}
              <TR className="border-t border-hairline-strong bg-surface-sunken">
                {visibleKeys.map((key) => {
                  const column = key as Column;
                  const numeric = COLUMNS[column]?.numeric ?? false;
                  let content: ReactNode = null;
                  if (column === 'event') {
                    content = (
                      <span className="pl-9">Total · {formatCount(headline.events)} events</span>
                    );
                  } else if (column === 'registrations') {
                    content = formatCount(headline.registrations);
                  } else if (column === 'attendees') {
                    content = formatCount(headline.attendees);
                  } else if (column === 'attendance') {
                    content = formatRate(headline.attendanceRate);
                  }
                  return (
                    <TD
                      key={key}
                      numeric={numeric}
                      className={numeric ? 'font-medium tabular-nums' : 'font-medium'}
                      {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                    >
                      {content}
                    </TD>
                  );
                })}
              </TR>
            </TBody>
          </Table>
        </TableWrap>
        {showMore}
      </div>

      {/* Phone */}
      <div className="p-3 md:hidden">
        <PiCardList
          items={shown}
          getKey={eventKey}
          render={(event) => {
            const id = eventKey(event);
            const open = expanded.has(id);
            return (
              <div className="min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <EventName event={event} />
                    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-muted">
                      <span className="whitespace-nowrap tabular-nums">
                        {eventDateText(event.startDate, event.endDate)}
                      </span>
                      <span className="truncate">{eventPlaceText(event)}</span>
                      <FormatBadge format={event.format} />
                    </div>
                  </div>
                  <div className="shrink-0">
                    <PiExpandButton expanded={open} onClick={() => toggle(id)} label={`${open ? 'Hide' : 'Show'} schools for ${event.name}`} />
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                  <PiCardField label="Registrations" value={formatCount(event.registrations)} />
                  <PiCardField label="Attendees" value={formatCount(event.attendees)} />
                  <PiCardField label="Attendance" value={formatRate(event.attendanceRate)} />
                  <PiCardField label="Schools" value={formatCount(event.participatingSchoolsCount)} />
                </div>
                {open && (
                  <div className="mt-3 border-t border-hairline pt-3">
                    <EventSchoolsPanel event={event} />
                  </div>
                )}
              </div>
            );
          }}
        />
        {showMore}
        <div className="mt-2 rounded-md border border-hairline-strong bg-surface-sunken p-3">
          <div className="font-medium">Total · {formatCount(headline.events)} events</div>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
            <PiCardField label="Registrations" value={formatCount(headline.registrations)} />
            <PiCardField label="Attendees" value={formatCount(headline.attendees)} />
            <PiCardField label="Attendance" value={formatRate(headline.attendanceRate)} />
          </div>
        </div>
      </div>
    </PiSection>
  );
}
